import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boton } from '../componentes/Boton';
import { Campo } from '../componentes/Campo';
import { Contenedor } from '../componentes/Contenedor';
import { Modal } from '../componentes/Modal';
import { BuscadorFiltros } from '../componentes/BuscadorFiltros';
import { Pestanas, PESTANAS_ESTADO, type EstadoVisible } from '../componentes/Pestanas';
import { Lista } from '../componentes/iconos';
import { useMigas } from '../lib/migas';
import { usePuede } from '../lib/auth';
import { ErrorApi } from '../lib/api';
import { useCrearCuestionario, useCuestionarios } from '../lib/cuestionarios';

/**
 * M5 — la biblioteca de cuestionarios.
 *
 * ── Lo que esta pantalla tiene que dejar claro de un vistazo ──
 * No es «cuántos cuestionarios hay». Es **cuál está vigente y cuál tiene trabajo
 * a medias**. Un cuestionario con un borrador abierto es uno que alguien empezó
 * a cambiar y no terminó: si eso no se ve en la lista, se descubre el día que se
 * intenta publicar y aparece un conflicto que nadie esperaba.
 */
export function Cuestionarios() {
  const [texto, setTexto] = useState('');
  /*
    El estado vive en una PESTAÑA, no en el panel de filtros. Ahí adentro era un
    valor por omisión invisible: la lista escondía los desactivados y la razón
    estaba plegada a dos clics.
  */
  const [estado, setEstado] = useState<EstadoVisible>('si');
  const [creando, setCreando] = useState(false);
  const ir = useNavigate();

  useMigas([{ texto: 'Cuestionarios' }]);

  const consulta = useCuestionarios({ texto, activo: estado });
  const puedeArmar = usePuede('cuestionarios.armar');

  return (
    <Contenedor>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-marino-800">Cuestionarios</h1>
        <p className="mt-1 text-sm text-marino-400">
          Los instrumentos con los que Copathy mide antes, durante y después de cada sesión.
        </p>
      </div>

      <div className="mb-5">
        <BuscadorFiltros
          busqueda={texto}
          alBuscar={setTexto}
          guia="Buscar por nombre o descripción"
          derecha={
            puedeArmar ? (
              <Boton onClick={() => setCreando(true)} className="py-3">
                Nuevo cuestionario
              </Boton>
            ) : undefined
          }
        />
      </div>

      <Pestanas
        etiqueta="Estado"
        pestanas={PESTANAS_ESTADO}
        activa={estado}
        alElegir={setEstado}
      />

      {consulta.isPending && <p className="py-16 text-center text-marino-400">Cargando…</p>}

      {consulta.isError && (
        <p className="py-16 text-center text-red-600">
          No pudimos cargar los cuestionarios. Recarga la página.
        </p>
      )}

      {consulta.data?.length === 0 && (
        <div className="rounded-2xl border border-marino-100 bg-white py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-marino-50 text-marino-300">
            <Lista size={24} />
          </div>
          <h2 className="font-semibold text-marino-700">
            {texto ? 'Ningún cuestionario coincide' : 'Todavía no hay cuestionarios'}
          </h2>
          <p className="mt-1 text-sm text-marino-400">
            {texto
              ? 'Prueba con otro nombre, o quita algún filtro.'
              : 'Arma el primero: se guarda como borrador y solo se publica cuando esté listo.'}
          </p>
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {consulta.data?.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => ir(`/cuestionarios/${c.id}`)}
            className={`group rounded-2xl border bg-white p-5 text-left transition
              hover:border-marino-300 hover:shadow-sm
              focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
              ${c.activo ? 'border-marino-100' : 'border-marino-100 opacity-70'}`}
          >
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="min-w-0 font-semibold text-marino-800">{c.nombre}</h2>
              {!c.activo && (
                <span className="rounded-full bg-marino-50 px-2.5 py-0.5 text-xs font-semibold text-marino-500">
                  Desactivado
                </span>
              )}
            </div>

            {c.descripcion && (
              <p className="mt-1 line-clamp-2 text-sm text-marino-400">{c.descripcion}</p>
            )}

            {/*
              Los dos estados que importan, y por qué son dos etiquetas y no una:
              un cuestionario puede estar publicado Y tener un borrador encima.
              Fundirlas en un solo rótulo obligaría a elegir cuál se pierde.
            */}
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-semibold">
              {c.versionPublicada ? (
                <span className="rounded-full bg-marino-700 px-2.5 py-1 text-white">
                  Publicado · v{c.versionPublicada}
                </span>
              ) : (
                <span className="rounded-full bg-marino-50 px-2.5 py-1 text-marino-500">
                  Sin publicar
                </span>
              )}
              {c.tieneBorrador && (
                <span className="rounded-full bg-naranja-100 px-2.5 py-1 text-marino-800">
                  Borrador abierto
                </span>
              )}
              <span className="text-marino-400">
                {c.preguntas} {c.preguntas === 1 ? 'pregunta' : 'preguntas'}
              </span>
            </div>
          </button>
        ))}
      </div>

      {creando && <ModalNuevo alCerrar={() => setCreando(false)} />}
    </Contenedor>
  );
}

function ModalNuevo({ alCerrar }: { alCerrar: () => void }) {
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [campos, setCampos] = useState<Record<string, string>>({});
  const crear = useCrearCuestionario();
  const ir = useNavigate();

  async function enviar() {
    setError(null);
    setCampos({});
    try {
      const c = await crear.mutateAsync({
        nombre: nombre.trim(),
        ...(descripcion.trim() ? { descripcion: descripcion.trim() } : {}),
      });
      // Se entra directo a armarlo: crear un cuestionario y quedarse en la lista
      // deja a la gente buscando en la lista lo que acaba de crear.
      ir(`/cuestionarios/${c.id}`);
    } catch (e) {
      if (e instanceof ErrorApi) {
        setError(e.message);
        setCampos(e.campos ?? {});
        return;
      }
      setError('No pudimos crear el cuestionario. Intenta de nuevo.');
    }
  }

  return (
    <Modal
      titulo="Nuevo cuestionario"
      ancho="medio"
      abierto
      hayCambios={nombre.trim().length > 0 || descripcion.trim().length > 0}
      alCerrar={alCerrar}
      pie={
        <>
          <Boton variante="secundario" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton onClick={() => void enviar()} cargando={crear.isPending}>
            Crear y armar
          </Boton>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-marino-500">
          Nace como <strong className="font-semibold text-marino-700">borrador</strong>. Nadie lo
          responde hasta que lo publiques.
        </p>

        {error && (
          <div
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        <Campo
          etiqueta="Nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          error={campos.nombre}
          autoFocus
        />
        <Campo
          etiqueta="Descripción (opcional)"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          error={campos.descripcion}
        />
      </div>
    </Modal>
  );
}
